import { useMemo } from 'react';

import { useProcessorsList } from '@/lib/api/processors';
import { useStoresList } from '@/lib/api/stores';

const STORE_ID_KEYS = new Set(['store_id', 'storeId']);
const STORE_NAME_KEYS = new Set(['store_name', 'storeName']);
const PROCESSOR_ID_KEYS = new Set(['processor_id', 'processorId']);
const PROCESSOR_NAME_KEYS = new Set(['processor_name', 'processorName', 'processor']);

export type ReportLookupMaps = {
  storesById: Record<string, string>;
  processorsById: Record<string, string>;
};

export const useReportLookupMaps = (): ReportLookupMaps => {
  const { data: storesData } = useStoresList({ pagination: { page: 1, pageSize: 500 } });
  const { data: processorsData } = useProcessorsList();
  const storesById = useMemo(
    () => Object.fromEntries((storesData?.stores ?? []).map((store) => [store.id, store.name])),
    [storesData?.stores],
  );
  const processorsById = useMemo(
    () =>
      Object.fromEntries(
        (processorsData ?? []).map((processor) => [processor.id, processor.name ?? processor.id]),
      ),
    [processorsData],
  );
  return { storesById, processorsById };
};

export const resolveLookupLabel = (
  key: string,
  value: string | number | null,
  maps: ReportLookupMaps,
): string | number | null => {
  if (value === null || value === undefined) return value;
  const text = String(value);
  if (STORE_ID_KEYS.has(key)) return maps.storesById[text] ?? text;
  if (PROCESSOR_ID_KEYS.has(key)) return maps.processorsById[text] ?? text;
  return value;
};

export const preferReadableNameColumns = <T extends { key: string }>(columns: T[]): T[] => {
  const hasStoreName = columns.some((col) => STORE_NAME_KEYS.has(col.key));
  const hasProcessorName = columns.some((col) => PROCESSOR_NAME_KEYS.has(col.key));
  return columns.filter((col) => {
    if (hasStoreName && STORE_ID_KEYS.has(col.key)) return false;
    if (hasProcessorName && PROCESSOR_ID_KEYS.has(col.key)) return false;
    return true;
  });
};

const isEntityId = (value: string) =>
  value.startsWith('store_') || value.startsWith('processor_') || value.startsWith('proc_');

export const resolveEntityLabel = (value: string, maps: ReportLookupMaps): string =>
  maps.storesById[value] ?? maps.processorsById[value] ?? value;

export const enrichSeriesLabel = (value: string, maps: ReportLookupMaps): string =>
  isEntityId(value) ? resolveEntityLabel(value, maps) : value;
